import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";

import Modal from "../../../components/Reusable/Modal";
import RichTextEditor from "../../../components/Reusable/RichTextEditor";
import {
  fetchSupport,
  createSupport,
  updateSupport,
  deleteSupport,
} from "../../../features/auth/store/supportSlice";
import { SupportItem } from "../../../features/auth/store/types/support-types";

interface SupportManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialEditItem?: SupportItem | null;
}

const SupportManagementModal: React.FC<SupportManagementModalProps> = ({
  isOpen,
  onClose,
  initialEditItem,
}) => {
  const dispatch = useAppDispatch();
  const items = useAppSelector((state: any) => state.support?.items ?? []);
  const loading = useAppSelector(
    (state: any) => state.support?.loading ?? false,
  );
  const saving = useAppSelector((state: any) => state.support?.saving ?? false);

  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [editingItem, setEditingItem] = useState<SupportItem | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const hasVisibleText = (html: string): boolean => {
    const stripped = html
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();
    return stripped.length > 0;
  };

  const resetForm = () => {
    setQuestion("");
    setAnswer("");
    setEditingItem(null);
  };

  useEffect(() => {
    if (!isOpen) return;
    dispatch(fetchSupport());
    if (initialEditItem) {
      setEditingItem(initialEditItem);
      setQuestion(initialEditItem.question);
      setAnswer(initialEditItem.answer);
    } else {
      resetForm();
    }
  }, [isOpen, initialEditItem]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!question.trim() || !hasVisibleText(answer)) {
      toast.error("Question and answer are required");
      return;
    }

    try {
      if (editingItem) {
        await dispatch(
          updateSupport({ id: editingItem.id, question, answer }),
        ).unwrap();
        toast.success("Support item updated successfully");
      } else {
        await dispatch(createSupport({ question, answer })).unwrap();
        toast.success("Support item created successfully");
      }
      resetForm();
    } catch (err: any) {
      toast.error(err || "Failed to save support item");
    }
  };

  const handleEdit = (item: SupportItem) => {
    setEditingItem(item);
    setQuestion(item.question);
    setAnswer(item.answer);
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    try {
      await dispatch(deleteSupport(id)).unwrap();
      toast.success("Support item deleted successfully");
      if (editingItem?.id === id) resetForm();
    } catch (err: any) {
      toast.error(err || "Failed to delete support item");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="w-full max-w-2xl mx-4 max-h-[92vh] flex flex-col p-4 sm:p-6"
    >
      <h2 className="text-lg sm:text-xl font-bold mb-4">Manage Support</h2>

      <div className="flex-1 overflow-y-auto pr-1 space-y-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block mb-1.5 text-xs sm:text-sm font-medium">
              Question
            </label>
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              required
              className="w-full p-2.5 sm:p-2 text-sm border rounded-lg dark:bg-white/10 dark:text-white border-gray-200 dark:border-zinc-800 focus:outline-none"
            />
          </div>

          <div>
            <label className="block mb-1.5 text-xs sm:text-sm font-medium">
              Answer
            </label>
            <div className="max-w-full overflow-hidden rounded-lg">
              <RichTextEditor
                value={answer}
                onChange={setAnswer}
                placeholder="Write answer..."
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2.5 text-sm font-medium bg-gradient-to-r from-[#027EC2] to-[#039EF2] text-white rounded-lg disabled:opacity-70 text-center"
            >
              {saving ? "Saving..." : editingItem ? "Update" : "Add"}
            </button>
            {editingItem && (
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2.5 text-sm font-medium border border-[#D0D5DD] dark:border-[#FFFFFF1A] rounded-lg text-center dark:text-white"
              >
                Cancel Edit
              </button>
            )}
          </div>
        </form>

        <div className="border border-[#D0D5DD] dark:border-[#FFFFFF1A] rounded-xl overflow-hidden bg-transparent">
          <div className="px-4 py-3 bg-gray-50 dark:bg-[#FFFFFF1A] font-semibold text-xs sm:text-sm text-gray-700 dark:text-white">
            Support Items
          </div>
          <div className="max-h-[260px] overflow-y-auto divide-y divide-[#D0D5DD] dark:divide-[#FFFFFF1A]">
            {loading ? (
              <div className="p-6 text-center text-sm text-gray-400">
                Loading...
              </div>
            ) : items.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400">
                No support items yet.
              </div>
            ) : (
              items.map((item: SupportItem) => (
                <div
                  key={item.id}
                  className="p-4 hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors"
                >
                  <p className="font-semibold text-sm mb-1 text-gray-900 dark:text-white break-words">
                    {item.question}
                  </p>
                  <div
                    className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 prose prose-sm max-w-none dark:prose-invert break-words line-clamp-3"
                    dangerouslySetInnerHTML={{ __html: item.answer }}
                  />
                  <div className="mt-3 flex items-center gap-4">
                    <button
                      type="button"
                      onClick={() => handleEdit(item)}
                      className="text-blue-600 dark:text-blue-400 font-medium text-xs sm:text-sm hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(item.id)}
                      disabled={deletingId === item.id}
                      className="text-red-600 dark:text-red-400 font-medium text-xs sm:text-sm hover:underline disabled:opacity-70"
                    >
                      {deletingId === item.id ? "Deleting..." : "Delete"}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SupportManagementModal;
